/**
 * Supervisor Dashboard Component
 *
 * Inbox-first dashboard for supervisor users (#101, #107 — RACI Compliance).
 * Two sections:
 *  - Pending material requests (RLS-gated SELECT on v_material_requests_with_items;
 *    approve/reject via MCP `approve_material_request` which accepts admin + supervisor).
 *  - Pending tool requests (read via toolRequestService; approve/reject via existing MCP tools).
 *
 * Smart (multi-item) material requests are visible but marked read-only because
 * `approve_smart_material_request` is admin-only. Supervisor sees them so nothing
 * is hidden; a small badge surfaces that admin must process them.
 *
 * @module pages/dashboard/SupervisorDashboard
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container, Row, Col, Card, Button, Badge, Spinner, Alert,
  Accordion, Breadcrumb, Modal, Form,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import { callMcpTool } from '../../services/mcpClient';
import {
  listPendingToolRequests,
  approveToolRequest,
  rejectToolRequest,
} from '../../services/toolRequestService';
import StatCard from '../../components/common/StatCard';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { formatDate } from '../../utils/formatters';
import { parseRequestItems } from '../../utils/materialSplitPreview';
import { useMaterialSplitPreview } from '../../hooks/useMaterialSplitPreview';
import {
  MaterialSplitBadges,
  MaterialSplitItemRow,
  MaterialSplitStockWarning,
  MaterialSimpleItemRow,
} from '../../components/materials/MaterialSplitPreview';
import './SupervisorDashboard.css';

// ----------------------------------------------------------------------------
// Material requests — direct Supabase read (RLS supervisor_view_material_requests
// from migration 060 lets supervisors see all pending rows).
// ----------------------------------------------------------------------------
const fetchPendingMaterialRequests = async () => {
  const { data, error } = await supabase
    .from('v_material_requests_with_items')
    .select(`
      *,
      project:projects(id, project_number, title),
      requester:profiles!material_requests_requested_by_fkey(id, full_name),
      inventory_item:inventory(id, name, sku, unit)
    `)
    .eq('status', 'pending')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

// Aprobación/rechazo de materiales (single-item) va por `callMcpTool`
// (`services/mcpClient`), la única implementación del fetch a
// `/api/mcp/tools/*`. Hasta acá esta página tenía su propia copia privada del
// helper — la octava — y era la única que no pasaba por `checkMcpAuthExpiry`,
// así que un 401 por sesión vencida terminaba en un toast genérico con el
// usuario todavía en pantalla creyéndose logueado (justo la falla que #389
// existe para evitar). La tool es `approve_material_request` (admin +
// supervisor).

// ============================================================================
// Component
// ============================================================================
const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const [processingMaterialId, setProcessingMaterialId] = useState(null);
  const [processingToolId, setProcessingToolId] = useState(null);

  // Reject modal state (shared between material + tool flows)
  const [rejectModal, setRejectModal] = useState(null); // { kind: 'material'|'tool', id, label }
  const [rejectReason, setRejectReason] = useState('');

  // -------------------- queries --------------------
  const {
    data: materialRequests = [],
    isLoading: loadingMaterial,
    error: materialError,
    refetch: refetchMaterial,
  } = useQuery({
    queryKey: ['supervisor-material-requests'],
    queryFn: fetchPendingMaterialRequests,
    staleTime: 30_000,
  });

  const {
    data: toolResult,
    isLoading: loadingTools,
    error: toolError,
    refetch: refetchTools,
  } = useQuery({
    queryKey: ['supervisor-tool-requests'],
    queryFn: listPendingToolRequests,
    staleTime: 30_000,
  });

  // Los dos Alert de abajo imprimían `error.message` tal cual: strings de
  // PostgREST del estilo "column projects.foo does not exist". Nombran objetos
  // internos, no dicen qué hacer, y en esta misma pantalla ya hubo el mismo
  // defecto con `source_input_text`. El detalle va a la consola.
  useEffect(() => {
    if (materialError) {
      // eslint-disable-next-line no-console
      console.error('[SupervisorDashboard] solicitudes de materiales', materialError);
    }
  }, [materialError]);
  useEffect(() => {
    if (toolError) {
      // eslint-disable-next-line no-console
      console.error('[SupervisorDashboard] solicitudes de herramientas', toolError);
    }
  }, [toolError]);
  const toolRequests = useMemo(() => toolResult?.data || [], [toolResult]);

  // #513 — MISMO preview que el inbox de admin: una sola query de stock y el
  // reparto sale de `planMaterialSplit`, el módulo que usa la aprobación. Antes
  // este conteo estaba copiado acá y en `MaterialRequestsInbox`, y las dos
  // copias mentían igual.
  const {
    isLoading: stockLoading,
    error: stockError,
    summarize,
  } = useMaterialSplitPreview(materialRequests);

  // -------------------- derived stats --------------------
  const stats = useMemo(() => ({
    materialPending: materialRequests.length,
    toolPending: toolRequests.length,
    totalPending: materialRequests.length + toolRequests.length,
  }), [materialRequests, toolRequests]);

  // -------------------- material handlers --------------------
  const handleApproveMaterial = useCallback(async (req) => {
    const items = parseRequestItems(req);
    // Smart requests (multi-item) currently require admin (approve_smart_material_request)
    if (items.length > 0) {
      toast.warn('Las solicitudes multi-ítem requieren aprobación de un administrador.');
      return;
    }

    setProcessingMaterialId(req.id);
    try {
      const { notices = [], error } = await callMcpTool('approve_material_request', {
        request_id: req.id,
        approved: true,
      });
      if (error) throw error;
      // Los `notices` de la tool dicen lo que NO hizo: para una externa de un
      // ítem, que no se consumió stock ni se generó compra (#783). "Solicitud
      // aprobada." a secas se leía como "pedida". El `message` de la tool no
      // se muestra: es inglés de máquina.
      toast.success(['Solicitud aprobada.', ...notices].join(' '));
      queryClient.invalidateQueries({ queryKey: ['supervisor-material-requests'] });
    } catch (err) {
      toast.error(err.message || 'Error al aprobar');
    } finally {
      setProcessingMaterialId(null);
    }
  }, [queryClient]);

  const openRejectMaterial = (req) => {
    setRejectReason('');
    setRejectModal({
      kind: 'material',
      id: req.id,
      label: `${req.project?.project_number || ''} — ${req.project?.title || 'Solicitud'}`,
    });
  };

  // -------------------- tool handlers --------------------
  const handleApproveTool = useCallback(async (req) => {
    setProcessingToolId(req.id);
    try {
      const { error } = await approveToolRequest({ request_id: req.id });
      if (error) throw error;
      toast.success('Solicitud de herramienta aprobada.');
      queryClient.invalidateQueries({ queryKey: ['supervisor-tool-requests'] });
    } catch (err) {
      toast.error(err.message || 'Error al aprobar');
    } finally {
      setProcessingToolId(null);
    }
  }, [queryClient]);

  const openRejectTool = (req) => {
    setRejectReason('');
    setRejectModal({
      kind: 'tool',
      id: req.id,
      label: req.tool?.name || 'Solicitud de herramienta',
    });
  };

  // -------------------- shared reject flow --------------------
  const submitReject = useCallback(async () => {
    if (!rejectReason.trim()) {
      toast.warn('Indicá un motivo para el rechazo.');
      return;
    }
    const { kind, id } = rejectModal;
    try {
      if (kind === 'material') {
        setProcessingMaterialId(id);
        const { error } = await callMcpTool('approve_material_request', {
          request_id: id,
          approved: false,
          rejection_reason: rejectReason.trim(),
        });
        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: ['supervisor-material-requests'] });
        toast.success('Solicitud rechazada.');
      } else {
        setProcessingToolId(id);
        const { error } = await rejectToolRequest({ request_id: id, reason: rejectReason.trim() });
        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: ['supervisor-tool-requests'] });
        toast.success('Solicitud de herramienta rechazada.');
      }
      setRejectModal(null);
      setRejectReason('');
    } catch (err) {
      toast.error(err.message || 'Error al rechazar');
    } finally {
      setProcessingMaterialId(null);
      setProcessingToolId(null);
    }
  }, [rejectReason, rejectModal, queryClient]);

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <DashboardLayout>
      <Container fluid className="supervisor-dashboard">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col lg={8}>
            <Breadcrumb className="mb-2">
              <Breadcrumb.Item active>
                <i className="bi bi-house-door me-1"></i>
                Dashboard
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Supervisor</Breadcrumb.Item>
            </Breadcrumb>
            <h1 className="h2 mb-1 fw-bold">
              <i className="bi bi-person-check-fill text-warning me-2"></i>
              Inbox del Supervisor
            </h1>
            <p className="text-muted mb-0">
              <i className="bi bi-person-circle me-1"></i>
              Bienvenido, {profile?.full_name || 'Supervisor'}. Revisá y aprobá las solicitudes pendientes del equipo.
            </p>
          </Col>
        </Row>

        {/* Stats */}
        <Row className="g-4 mb-4">
          <Col md={4}>
            <StatCard
              title="Solicitudes Pendientes"
              value={stats.totalPending}
              icon="bi-inbox-fill"
              color={stats.totalPending > 0 ? 'warning' : 'success'}
            />
          </Col>
          <Col md={4}>
            <StatCard
              title="Materiales"
              value={stats.materialPending}
              icon="bi-box-seam"
              color="primary"
            />
          </Col>
          <Col md={4}>
            <StatCard
              title="Herramientas"
              value={stats.toolPending}
              icon="bi-tools"
              color="info"
            />
          </Col>
        </Row>

        {/* Material Requests */}
        <Card className="shadow-sm mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center bg-white">
            <h5 className="mb-0 fw-semibold">
              <i className="bi bi-box-seam me-2 text-primary"></i>
              Solicitudes de Materiales
              {stats.materialPending > 0 && (
                <Badge bg="danger" pill className="ms-2">{stats.materialPending}</Badge>
              )}
            </h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate('/admin/solicitudes-materiales')}
            >
              Ver todas <i className="bi bi-arrow-right ms-1"></i>
            </Button>
          </Card.Header>
          <Card.Body>
            {loadingMaterial && (
              <div className="text-center py-4"><Spinner /></div>
            )}
            {materialError && (
              <Alert variant="danger" data-testid="supervisor-material-error">
                <Alert.Heading className="h6 mb-1">
                  No se pudieron cargar las solicitudes de materiales
                </Alert.Heading>
                <p className="small mb-2">
                  Es un problema de conexión con el servidor, no una bandeja vacía.
                  Volvé a intentarlo; si sigue fallando, avisá al administrador.
                </p>
                <Button variant="outline-danger" size="sm" onClick={() => refetchMaterial()}>
                  <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                  Reintentar
                </Button>
              </Alert>
            )}
            {!loadingMaterial && !materialError && materialRequests.length === 0 && (
              <div className="text-center py-4 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                <p className="mb-0">No tenés solicitudes de materiales pendientes.</p>
                <p className="small mb-0">Te avisaremos cuando lleguen.</p>
              </div>
            )}
            {!loadingMaterial && materialRequests.length > 0 && (
              <MaterialSplitStockWarning error={stockError} />
            )}
            {!loadingMaterial && materialRequests.length > 0 && (
              <Accordion>
                {materialRequests.map((req, i) => {
                  const items = parseRequestItems(req);
                  const summary = summarize(items);
                  const isSmart = items.length > 0;
                  const isProcessing = processingMaterialId === req.id;

                  return (
                    <Accordion.Item key={req.id} eventKey={String(i)} className="mb-2 border-0 shadow-sm rounded">
                      <Accordion.Header>
                        <Row className="w-100 align-items-center g-1 me-3">
                          <Col xs={12} sm={5}>
                            <span className="fw-semibold small">
                              {req.project?.project_number || 'PRJ-—'} — {req.project?.title || 'Sin proyecto'}
                            </span>
                          </Col>
                          {/* xs={12}: a 375px la fila de tres columnas dejaba
                              la tercera (badges) en ~150px útiles y los badges
                              se cortaban. Debajo de `sm` cada bloque ocupa el
                              ancho completo y se apila. */}
                          <Col xs={12} sm={4} className="text-muted small">
                            {req.requester?.full_name || 'Solicitante desconocido'}
                            <br />
                            <span className="supervisor-request-date">{formatDate(req.created_at)}</span>
                          </Col>
                          <Col xs={12} sm={3} className="supervisor-request-badges d-flex gap-1 justify-content-end flex-wrap">
                            {isSmart ? (
                              <>
                                <MaterialSplitBadges summary={summary} loading={stockLoading} />
                                <Badge bg="secondary" title="Requiere admin">
                                  admin
                                </Badge>
                              </>
                            ) : (
                              <Badge bg="secondary">1 ítem</Badge>
                            )}
                          </Col>
                        </Row>
                      </Accordion.Header>
                      <Accordion.Body>
                        {req.source_input_text && (
                          <blockquote
                            className="text-muted small border-start border-3 ps-2 mb-3"
                            style={{ borderColor: '#dee2e6', fontStyle: 'italic' }}
                          >
                            "{req.source_input_text.substring(0, 200)}
                            {req.source_input_text.length > 200 ? '…' : ''}"
                          </blockquote>
                        )}

                        {/* Multi-item list — #513: reparto real por ítem, no badge binario */}
                        {isSmart && (
                          <div className="mb-3">
                            {summary.plans.map((plan, j) => (
                              <MaterialSplitItemRow
                                key={plan.item?.id || j}
                                plan={plan}
                                loading={stockLoading}
                              />
                            ))}
                          </div>
                        )}

                        {/* Solicitud de un ítem (externa, de catálogo o sin
                            material): el MISMO componente que el inbox admin.
                            Esta página ya sabía dibujar la externa y el inbox
                            no — la duplicación de #513 en el camino de un
                            ítem, resuelta compartiendo. */}
                        {!isSmart && (
                          <div className="mb-3">
                            <MaterialSimpleItemRow req={req} />
                          </div>
                        )}

                        {req.notes && (
                          <Alert variant="info" className="small py-2 mb-3">
                            {req.notes}
                          </Alert>
                        )}

                        {isSmart ? (
                          <Alert variant="warning" className="small py-2 mb-0">
                            <i className="bi bi-info-circle me-1"></i>
                            Esta solicitud tiene varios ítems con compra/stock. La aprobación final
                            la procesa un administrador.
                          </Alert>
                        ) : (
                          <div className="d-flex gap-2">
                            <Button
                              variant="success"
                              size="sm"
                              disabled={isProcessing}
                              onClick={() => handleApproveMaterial(req)}
                              data-testid={`approve-material-${req.id}`}
                            >
                              {isProcessing ? (
                                <Spinner size="sm" />
                              ) : (
                                <><i className="bi bi-check-lg me-1" />Aprobar</>
                              )}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={isProcessing}
                              onClick={() => openRejectMaterial(req)}
                              data-testid={`reject-material-${req.id}`}
                            >
                              <i className="bi bi-x-lg me-1" />Rechazar
                            </Button>
                          </div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </Card.Body>
        </Card>

        {/* Tool Requests */}
        <Card className="shadow-sm mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center bg-white">
            <h5 className="mb-0 fw-semibold">
              <i className="bi bi-tools me-2 text-info"></i>
              Solicitudes de Herramientas
              {stats.toolPending > 0 && (
                <Badge bg="danger" pill className="ms-2">{stats.toolPending}</Badge>
              )}
            </h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate('/herramientas')}
            >
              Ver herramientas <i className="bi bi-arrow-right ms-1"></i>
            </Button>
          </Card.Header>
          <Card.Body>
            {loadingTools && (
              <div className="text-center py-4"><Spinner /></div>
            )}
            {toolError && (
              <Alert variant="danger" data-testid="supervisor-tool-error">
                <Alert.Heading className="h6 mb-1">
                  No se pudieron cargar las solicitudes de herramientas
                </Alert.Heading>
                <p className="small mb-2">
                  Es un problema de conexión con el servidor, no una bandeja vacía.
                  Volvé a intentarlo; si sigue fallando, avisá al administrador.
                </p>
                <Button variant="outline-danger" size="sm" onClick={() => refetchTools()}>
                  <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                  Reintentar
                </Button>
              </Alert>
            )}
            {!loadingTools && !toolError && toolRequests.length === 0 && (
              <div className="text-center py-4 text-muted">
                <i className="bi bi-tools fs-1 d-block mb-2"></i>
                <p className="mb-0">No tenés solicitudes de herramientas pendientes.</p>
                <p className="small mb-0">Te avisaremos cuando lleguen.</p>
              </div>
            )}
            {!loadingTools && toolRequests.length > 0 && (
              <Accordion>
                {toolRequests.map((req, i) => {
                  const isProcessing = processingToolId === req.id;
                  return (
                    <Accordion.Item key={req.id} eventKey={String(i)} className="mb-2 border-0 shadow-sm rounded">
                      <Accordion.Header>
                        <Row className="w-100 align-items-center g-1 me-3">
                          <Col xs={12} sm={5}>
                            <span className="fw-semibold small">
                              <i className="bi bi-wrench-adjustable me-1 text-muted"></i>
                              {req.tool?.name || 'Herramienta'}
                              {req.tool?.serial_number && (
                                <small className="text-muted ms-1">#{req.tool.serial_number}</small>
                              )}
                            </span>
                          </Col>
                          <Col xs={12} sm={4} className="text-muted small">
                            {req.requester?.full_name || 'Solicitante desconocido'}
                            <br />
                            <span className="supervisor-request-date">{formatDate(req.created_at)}</span>
                          </Col>
                          <Col xs={12} sm={3} className="supervisor-request-badges d-flex gap-1 justify-content-end flex-wrap">
                            {req.project?.project_number && (
                              <Badge bg="primary" title="Proyecto">
                                {req.project.project_number}
                              </Badge>
                            )}
                            {req.tool?.requires_approval && (
                              <Badge bg="warning" text="dark">aprob.</Badge>
                            )}
                          </Col>
                        </Row>
                      </Accordion.Header>
                      <Accordion.Body>
                        {req.reason && (
                          <p className="small mb-2">
                            <span className="fw-semibold">Motivo:</span> {req.reason}
                          </p>
                        )}
                        {req.project?.title && (
                          <p className="small mb-2">
                            <span className="fw-semibold">Proyecto:</span>{' '}
                            {req.project.project_number} — {req.project.title}
                          </p>
                        )}
                        {req.requested_location?.label && (
                          <p className="small mb-2">
                            <span className="fw-semibold">Destino:</span>{' '}
                            {req.requested_location.label}
                          </p>
                        )}
                        <div className="d-flex gap-2">
                          <Button
                            variant="success"
                            size="sm"
                            disabled={isProcessing}
                            onClick={() => handleApproveTool(req)}
                            data-testid={`approve-tool-${req.id}`}
                          >
                            {isProcessing ? (
                              <Spinner size="sm" />
                            ) : (
                              <><i className="bi bi-check-lg me-1" />Aprobar</>
                            )}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={isProcessing}
                            onClick={() => openRejectTool(req)}
                            data-testid={`reject-tool-${req.id}`}
                          >
                            <i className="bi bi-x-lg me-1" />Rechazar
                          </Button>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </Card.Body>
        </Card>

        {/* Helper Card */}
        <Alert variant="info" className="shadow-sm border-0">
          <div className="d-flex align-items-center">
            <i className="bi bi-info-circle fs-3 me-3 text-info"></i>
            <div>
              <strong>RACI — Supervisor</strong>
              <p className="mb-0 small">
                Aprobás las solicitudes del equipo (Líder → Supervisor → Adm).
                Las solicitudes multi-ítem (compra + stock) se elevan al admin.
              </p>
            </div>
          </div>
        </Alert>
      </Container>

      {/* Reject modal — shared between material + tool flows */}
      <Modal show={!!rejectModal} onHide={() => setRejectModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Rechazar solicitud</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejectModal && (
            <p className="text-muted small mb-2">
              <strong>Solicitud:</strong> {rejectModal.label}
            </p>
          )}
          <Form.Group>
            <Form.Label>Motivo del rechazo</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explicá brevemente por qué la solicitud no procede…"
              autoFocus
            />
            <Form.Text className="text-muted">
              Este mensaje se envía al solicitante en la notificación.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectModal(null)}>Cancelar</Button>
          <Button variant="danger" onClick={submitReject} disabled={!rejectReason.trim()}>
            Confirmar rechazo
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default SupervisorDashboard;
